/**
 * 레벨 데이터 -> three.js 씬 구축.
 *
 * 표면은 materials.ts 의 절차 PBR 재질을 공유하고, UV 는 실제 크기로 다시 씌운다
 * (텍셀 밀도 통일). 외부 에셋 0개 — Tauri 번들이 가볍고 로딩이 없다.
 */

import * as THREE from 'three';
import { LIGHT_SLOTS } from './budget';
import { OUT } from './portals';
import { bootMark } from './boot';
import { park } from './lightPool';
import { CurtainSystem } from './curtainSim';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import {
  DOORWAYS,
  FURNITURE,
  PIECES,
  PROPS,
  BUILDINGS,
  ROOMS,
  WALLS,
  WALL_H,
  WALL_T,
  WINDOWS,
  WIN_Y,
  roomAt,
  type FurnDef,
  type RoomDef,
} from '../../shared/level';
import { surfaces, worldUV, type SurfaceId } from './materials';
import { buildPiece, PIECE_BY_ID, placeSeed } from './furniture';
import { FINISH_BY_ID } from '../../shared/finishes';
import type { WallDef } from '../../shared/level-defs';
import type { PieceDef } from '../../shared/level-defs';
import { pieceKey } from './placeOverrides';
import { ContactShadows } from './decals';

export interface Collider {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  enabled: boolean;
  /** 원기둥 가구는 사각형으로 막으면 모서리에서 어색하게 걸린다 */
  circle?: { x: number; z: number; r: number };
}

export interface BuiltWorld {
  root: THREE.Group;
  colliders: Collider[];
  /** 실시간 커튼 — 매 프레임 `update(dt, 눈 위치, root)` (curtainSim.ts) */
  curtains: CurtainSystem;
  /**
   * **먼 방의 가구를 그리지 않는다** (2026-09-15, Chris: 「여전히 렉이 좀 심한데」). 가구는 조각마다 개별
   * 메시라 여섯 집을 채우자 draw call 이 150→426 이 됐다. 절두체 컬링은 벽 뒤를 못 거르므로 거리로 자른다.
   * 0.5초에 한 번이면 충분하다. 반경은 게임 24m, 에디터 30m
   */
  cullPieces: (at: THREE.Vector3, radius: number) => void;
  /**
   * **보이는 방만** (portals.ts, 2026-09-28). null 이면 방으로 거르지 않는다. 거리 컬링(cullPieces)과 곱해진다 — 둘 다 통과해야 보인다.
   * 방이 아닌 곳(바깥)의 가구는 OUT 이 보일 때만
   */
  setVisibleRooms: (rooms: Set<string> | null) => void;
  /** roomId -> 대표 조명 (부속 조명은 userData.siblings) */
  lights: Map<string, THREE.SpotLight>;
  ceiling: THREE.Group;
  surfaceOf: (id: SurfaceId) => THREE.MeshStandardMaterial;
  /** 벽(+몰딩·마감)만 다시 굽는다 — 창·문을 옮겼을 때. 바닥·가구·조사물은 그대로 둔다 */
  rebuildWallMesh: () => void;
  /** 바닥만 다시 굽는다 — 방 바닥재를 바꿨을 때 */
  rebuildFloorMesh: () => void;
  /** 방 조명 색·세기를 그 자리에서 바꾼다 — 마감 세트를 바꿨을 때 */
  setRoomLight: (roomId: string, color: string, k?: number) => void;
  /** 방별 조명 on/off 를 시야에 맞춰 갱신 */
  cullLights: (
    roomId: string | null,
    openDoors: Set<string>,
    switches: Record<string, boolean>,
    all?: boolean,
    /** 플레이어 위치 — 슬롯이 모자랄 때 가까운 조명을 고르는 기준 */
    at?: THREE.Vector3 | null,
  ) => void;
}

/**
 * 정적 지오메트리 배칭.
 *
 * 저택은 벽/몰딩/문틀/가구가 전부 움직이지 않는다. 그런데 메시로 하나씩 두면
 * 메인 패스뿐 아니라 **그림자 라이트 개수만큼 반복**해서 그려진다
 * (측정: 메시 187개 -> draw call 1035회, 그중 80%가 그림자 패스).
 * 삼각형은 6.5k 뿐이라 GPU 연산이 아니라 draw call 오버헤드가 병목이므로,
 * 같은 재질끼리 하나의 지오메트리로 합쳐 draw call 자체를 없앤다.
 */
class StaticBatcher {
  private buckets = new Map<THREE.Material, THREE.BufferGeometry[]>();

  add(geo: THREE.BufferGeometry, mat: THREE.Material, position: THREE.Vector3, rotationY = 0) {
    const g = geo.clone();
    if (rotationY) g.rotateY(rotationY);
    g.translate(position.x, position.y, position.z);
    geo.dispose();
    const list = this.buckets.get(mat);
    if (list) list.push(g);
    else this.buckets.set(mat, [g]);
  }

  flush(root: THREE.Group, castShadow = true, cull = false) {
    for (const [mat, geos] of this.buckets) {
      const merged = geos.length === 1 ? geos[0] : mergeGeometries(geos, false);
      if (!merged) continue;
      if (geos.length > 1) for (const g of geos) g.dispose();
      const mesh = new THREE.Mesh(merged, mat);
      mesh.castShadow = castShadow;
      mesh.receiveShadow = true;
      // 합쳐진 덩어리는 저택 전체를 덮으므로 프러스텀 컬링을 시도할 이유가 없다 — 방 단위 덩어리(cull)는 예외
      mesh.frustumCulled = cull;
      mesh.matrixAutoUpdate = false;
      root.add(mesh);
    }
    this.buckets.clear();
  }
}

/**
 * 건축 조각을 StaticBatcher 에 **굽는다** — 씬에 얹지 않는다.
 *
 * 가구는 그룹으로 얹어도 된다(한 점에 슬롯 3~5개). 건축은 개수가 다르다 — 창이 37장,
 * 벽이 203 세그먼트라 그룹으로 얹으면 draw call 이 수백 개 늘어난다. 재질을 키로
 * 병합해 두면 창 37장의 창틀이 **메시 하나**가 된다. 조각의 디테일은 그대로 살고
 * draw call 은 늘지 않는다. 근거는 docs/ARCHITECTURE_CATALOG.md.
 *
 * 조각은 dims 별로 한 번만 깎아 캐시한다 — 창 37장이 대개 두세 종류다.
 */
/**
 * **문짝 전용 재질.**
 *
 * 가구 슬롯의 `walnut` 은 darkwood 앨비도 텍스처(#42301f 계열, 선형 0.05 안팎)에
 * 틴트를 **곱한다**. 최종 반사율이 1~3% 라 숯보다 어둡다. 실내에서는 등이 코앞에
 * 있어 읽히지만, 밤 마을 현관에서는 등이 1m 옆에 있어도 **완전한 검정**으로 나왔다
 * — 마을의 문이 전부 뚫린 구멍처럼 보인 진짜 원인이 이것이다(문짝은 처음부터
 * 그려지고 있었다. 놋쇠 손잡이만 점처럼 빛나던 게 증거였다).
 *
 * 그래서 문짝은 앨비도 맵을 쓰지 않고 색을 직접 준다. 결(노멀)과 거칠기 맵은
 * 그대로 가져와 판때기로 보이지 않게 한다.
 */
let doorMats: { leaf: THREE.Material; frame: THREE.Material } | null = null;
function doorLeafMaterials(renderer: THREE.WebGLRenderer) {
  if (doorMats) return doorMats;
  const dw = surfaces(renderer).get('darkwood')!;
  const mk = (color: string, rough: number) => {
    const m = new THREE.MeshStandardMaterial({
      color,
      roughness: rough,
      metalness: 0,
      normalMap: dw.maps.normal,
      normalScale: new THREE.Vector2(0.55, 0.55),
      roughnessMap: dw.maps.roughness,
      vertexColors: true,
    });
    return m;
  };
  // 니스 칠한 소나무 문짝. 굽은 널 사이 선대는 조금 더 짙게 둔다.
  doorMats = { leaf: mk('#a8794e', 0.58), frame: mk('#8c6039', 0.52) };
  return doorMats;
}

const bakeCache = new Map<string, { geo: THREE.BufferGeometry; mat: THREE.Material }[]>();

function bakePiece(
  batch: StaticBatcher,
  id: string,
  dims: Record<string, number>,
  pos: THREE.Vector3,
  rotationY: number,
  renderer: THREE.WebGLRenderer,
) {
  const key = `${id}|${JSON.stringify(dims)}`;
  let parts = bakeCache.get(key);
  if (!parts) {
    const piece = PIECE_BY_ID[id];
    if (!piece) {
      console.warn('[건축] 조각이 없다:', id);
      return;
    }
    const { group } = buildPiece(piece, renderer, dims);
    /* **여닫이 부품(`open:*` 그룹) 안의 메시도 닫힌 자세로 굽는다** (2026-09-16).
     * 문짝·창짝·블라인드 슬랫이 movable 이 된 뒤, 직계 자식만 보면 그룹 속 메시가 빠져
     * 굽은 문이 「벽에 뚫린 구멍」이 된다. 닫힌 자세(그룹 변환 없음)로 굽히면 정적 문은
     * 예전과 똑같이 서 있고, 에디터에 놓인 조각만 젖힐 수 있다. */
    const collected: { geo: THREE.BufferGeometry; mat: THREE.Material }[] = [];
    group.traverse((o) => {
      if (o instanceof THREE.Mesh && o.material) {
        collected.push({ geo: o.geometry, mat: o.material as THREE.Material });
      }
    });
    parts = collected;
    if (id === 'door_panel') {
      const dm = doorLeafMaterials(renderer);
      parts = parts.map((q) => {
        const hex = (q.mat as THREE.MeshStandardMaterial).color?.getHexString?.();
        // 골조는 walnut(짙은 쪽), 널판은 oak(연한 쪽) — 슬롯으로 갈라 입힌다.
        // movable 병합으로 메시가 하나씩이라 방출 순서로는 못 가른다.
        if (hex === '8a6142') return { geo: q.geo, mat: dm.frame };
        if (hex === 'b98f5f') return { geo: q.geo, mat: dm.leaf };
        return q;
      });
    }
    bakeCache.set(key, parts);
  }
  // batch.add 는 넘긴 지오메트리를 clone 한 뒤 원본을 dispose 한다.
  // 캐시를 살려 두려면 여기서 먼저 복제해 넘겨야 한다.
  for (const p of parts) batch.add(p.geo.clone(), p.mat, pos, rotationY);
}

/**
 * 문 prop id -> 그 문이 여는 방.
 * 예전에는 저택 문 이름이 박혀 있어서 다른 레벨에서는 컬링이 통째로 헛돌았다.
 * 문은 벽 위에 서 있으므로, 문 양옆을 찍어서 복도가 아닌 쪽을 고르면 된다.
 */
/* 로드 시점이 아니라 처음 쓸 때 만든다 — 배럴(PROPS)이 고른 에피소드로 바뀐 뒤여야 한다. 레벨이 바뀌면 다시 만든다 */
let doorRoomFor: unknown = null;
let doorRoomMap: Record<string, string> = {};
function doorRoom(): Record<string, string> {
  if (doorRoomFor !== PROPS) { doorRoomFor = PROPS; doorRoomMap = computeDoorRoom(); }
  return doorRoomMap;
}
const computeDoorRoom = (): Record<string, string> => Object.fromEntries(
  PROPS.filter((p) => p.kind === 'door')
    .map((p) => {
      const here = roomAt(p.pos[0], p.pos[2])?.id;
      for (const [dx, dz] of [
        [0, -0.6],
        [0, 0.6],
        [-0.6, 0],
        [0.6, 0],
      ]) {
        const side = roomAt(p.pos[0] + dx, p.pos[2] + dz);
        if (side && side.id !== here) return [p.id, side.id];
      }
      return [p.id, ''];
    })
    .filter(([, room]) => room),
);

function floorSurface(room: RoomDef): SurfaceId {
  // 레벨이 지정하면 그것을 쓴다. 예전에는 저택의 방 id 가 박혀 있어서 다른 레벨은
  // 전부 마루가 됐다 (보일러실도 목공방도 마루였다).
  if (room.floor) return room.floor;
  return room.id === 'kitchen' ? 'tile' : room.id === 'cellar' ? 'concrete' : 'oak';
}

/** 가구 표면: 나무는 절차 재질, 그 외는 단색 + 약한 광택 */
function furnitureMaterial(f: FurnDef, cache: Map<string, THREE.MeshStandardMaterial>) {
  const key = `${f.color}|${f.rough ?? 0.85}|${f.metal ?? 0}`;
  let m = cache.get(key);
  if (!m) {
    m = new THREE.MeshStandardMaterial({
      color: f.color,
      roughness: f.rough ?? 0.85,
      metalness: f.metal ?? 0.02,
    });
    cache.set(key, m);
  }
  return m;
}

/**
 * 두 점 사이를 **벽이 가로막는가.** 밖에서 안쪽 물건을 집는 것을 막는 데 쓴다.
 *
 * 예전에는 「같은 방인가」만 봤고, 1.2m 안쪽은 문간에 걸친 물건 때문에 봐줬다.
 * 벽 두께가 그보다 얇아서 **바깥 벽에 붙어 서면 안쪽 물건이 그대로 잡혔다.**
 * 방 판정 대신 실제로 선을 그어 본다.
 *
 * 개구부는 통과시킨다 — `wallRun` 이 문 자리를 빼고 토막을 내므로 문간으로는
 * 자연히 손이 닿고, 창은 `gap:'window'` 토막이 그 자리를 덮고 있어 막힌다.
 * (유리가 있는 자리다. 창 너머로 서랍을 여는 게 더 이상하다.)
 */
export function wallBetween(ax: number, az: number, bx: number, bz: number): boolean {
  for (const w of WALLS) {
    if (w.gap === 'door') continue;
    const d1x = bx - ax, d1z = bz - az;
    const d2x = w.x2 - w.x1, d2z = w.z2 - w.z1;
    const den = d1x * d2z - d1z * d2x;
    if (Math.abs(den) < 1e-9) continue;          // 나란한 벽은 못 가로막는다
    const sx = w.x1 - ax, sz = w.z1 - az;
    const t = (sx * d2z - sz * d2x) / den;       // 시선 위 위치
    const u = (sx * d1z - sz * d1x) / den;       // 벽 위 위치
    if (t > 0.02 && t < 0.98 && u >= 0 && u <= 1) return true;
  }
  return false;
}

export interface BuildWorldOpts {
  /**
   * **정적 가구를 방 단위로 병합한다** (2026-09-15). 가구는 조각마다 메시 여럿이라 여섯 집을 채우자
   * draw call 이 150→400 이 됐다. 여닫이·GLB·프롭이 붙는 것·담는 곳만 그룹으로 남기고 나머지는 방마다
   * 재질별 한 덩어리로. **에디터는 끈다** — 집어서 옮기려면 낱개여야 한다. 게임은 켠다.
   */
  batchPieces?: boolean;
  /**
   * **방 점광(필)을 방마다 다 켠다** — WebGPU + ClusteredLighting(gpu.ts)이면 점광 수가 픽셀 비용을 안 늘린다.
   * 스포트는 클러스터가 못 받아 슬롯 그대로다
   */
  clusteredPoints?: boolean;
}

export function buildWorld(scene: THREE.Scene, renderer: THREE.WebGLRenderer, opts: BuildWorldOpts = {}): BuiltWorld {
  const shadows = renderer.shadowMap.enabled;
  const root = new THREE.Group();
  scene.add(root);
  const colliders: Collider[] = [];
  const lights = new Map<string, THREE.SpotLight>();
  const roomLights = new Map<string, THREE.Light[]>();

  /**
   * **조명 풀.** 방 조명은 씬에 직접 들어가지 않는다 — 여기 고정 슬롯에 위치·색·세기만
   * 갈아 끼운다.
   *
   * 왜: three 는 활성 조명 **개수**로 셰이더 프로그램 캐시 키를 만든다. 그래서 개수가
   * 변하면(방에 들어가 등이 켜지면) 씬의 모든 재질이 다시 컴파일되어 화면이 몇 초 멎고,
   * 반대로 스톨을 피하려고 전부 켜 두면 이 레벨의 조명 78개가 영구 활성이 되어 프래그먼트
   * 셰이더가 매 픽셀 76개를 돈다 — **실제 GPU 에서 탭이 죽었다.** 두 실패의 공통 원인이
   * 「개수가 내 손에 없다」는 것이고, 풀은 개수를 못 박아 둘을 동시에 없앤다.
   *
   * 슬롯 수는 한 번에 보이는 방(내 방 + 홀 + 열린 문 너머)을 덮을 만큼이면 된다. 넘치면 **가까운 것이 이긴다** —
   * 문틈으로 보이는 먼 방이 조금 어두워지는 것은 눈에 안 띄지만, 개수가 흔들리는 것은 스톨이나 죽음으로 눈에 띈다.
   *
   * **스포트 6 · 포인트 3** (2026-09-26, 12 · 10 에서). 빈 슬롯도 세기 0 으로 픽셀마다 계산된다 — 방 안에서 실제로
   * 켜지는 것은 스포트 2~4 · 포인트 1 이었다. 맥북 M2 Pro 게임 방 안: 점광·스포트 합이 **16 이하면 77fps, 17 이면
   * 50fps**(절벽). 조명 배분과 합은 budget.ts 가 갖고, 배포 전에 `npm run budget` 이 실제 씬을 센다.
   * 조감도(M)에선 켜지는 방이 줄어든다(53개 방 중 원래도 12개뿐이었다)
   */
  const SPOT_SLOTS = LIGHT_SLOTS.roomSpot;
  const POINT_SLOTS = opts.clusteredPoints ? ROOMS.length : LIGHT_SLOTS.roomPoint;
  /** 그림자를 질 수 있는 스포트 슬롯 수. 슬롯마다 렌더 패스가 한 번 더 돈다 */
  const SHADOW_SLOTS = 2;

  /** 풀에 꽂힐 후보. 실제 조명 객체를 '값'으로만 쓴다(씬에 없다) */
  interface LightSrc {
    light: THREE.Light;
    room: string;
    /** 방의 대표 조명인가 — 조감도에서 방마다 하나씩 고를 때 쓴다 */
    primary: boolean;
  }
  const srcs: LightSrc[] = [];

  const spotSlots: THREE.SpotLight[] = [];
  const pointSlots: THREE.PointLight[] = [];
  for (let i = 0; i < SPOT_SLOTS; i++) {
    const s = new THREE.SpotLight('#ffffff', 0, 16, Math.PI / 2.4, 0.62, 1.85);
    s.castShadow = shadows && i < SHADOW_SLOTS;
    s.shadow.mapSize.set(1024, 1024);
    s.shadow.camera.near = 0.4;
    s.shadow.camera.far = 11;
    s.shadow.bias = -0.0012;
    s.shadow.normalBias = 0.025;
    root.add(s);
    root.add(s.target);
    spotSlots.push(s);
  }
  for (let i = 0; i < POINT_SLOTS; i++) {
    const p = new THREE.PointLight('#ffffff', 0, 16, 1.8);
    p.castShadow = false;
    park(p);
    root.add(p);
    pointSlots.push(p);
  }
  const surf = surfaces(renderer);
  bootMark('세계: 재질(절차 텍스처)');
  const furnCache = new Map<string, THREE.MeshStandardMaterial>();
  const get = (id: SurfaceId) => surf.get(id)!;

  const batch = new StaticBatcher();
  const V = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);

  /* --- 바닥 ---
   * **벽과 같은 이유로 제 그룹에 담는다** (2026-09-14, Chris: 「벽지랑 바닥재 바꾸면 렉 또
   * 씹오지네」). 바닥재를 바꾸면 세계를 통째로 다시 짓고 있었다 — 가구 400점까지.
   * 방 42개짜리 판때기라 다시 굽는 데 몇 ms 면 끝난다. */
  const floorGroup = new THREE.Group(); floorGroup.name = 'floors';
  root.add(floorGroup);
  const emitFloors = (bt: StaticBatcher) => {
    for (const r of ROOMS) {
      const s = get(floorSurface(r));
      const geo = worldUV(new THREE.PlaneGeometry(r.x2 - r.x1, r.z2 - r.z1), s.tile);
      geo.rotateX(-Math.PI / 2);
      bt.add(geo, s.material, V((r.x1 + r.x2) / 2, r.y ?? 0, (r.z1 + r.z2) / 2));
    }
  };
  const floorBatch = new StaticBatcher();
  emitFloors(floorBatch);
  floorBatch.flush(floorGroup);
  /** 바닥만 다시 굽는다 — 바닥재를 바꿨을 때. 재질은 공용물이라 버리지 않는다 */
  const rebuildFloorMesh = () => {
    floorGroup.traverse((o2) => { const m = o2 as THREE.Mesh; if (m.geometry) m.geometry.dispose(); });
    floorGroup.clear();
    const bt = new StaticBatcher();
    emitFloors(bt);
    bt.flush(floorGroup);
  };

  // --- 천장 (3인칭 전환 시 숨김) ---
  const ceiling = new THREE.Group();
  const ceilS = get('ceiling');
  {
    const parts: THREE.BufferGeometry[] = [];
    for (const r of ROOMS) {
      const g = worldUV(new THREE.PlaneGeometry(r.x2 - r.x1, r.z2 - r.z1), ceilS.tile);
      g.rotateX(Math.PI / 2);
      g.translate((r.x1 + r.x2) / 2, (r.y ?? 0) + WALL_H, (r.z1 + r.z2) / 2);
      parts.push(g);
    }
    const merged = mergeGeometries(parts, false)!;
    for (const g of parts) g.dispose();
    const c = new THREE.Mesh(merged, ceilS.material);
    c.receiveShadow = true;
    c.frustumCulled = false;
    ceiling.add(c);

    /* **천장에도 무엇이 있어야 한다** (2026-09-14, Chris 추천 6번). 맨판이면 위를 볼 때 CG 다.
     * 작업장·창고(shop·store 세트)엔 벽 따라 전선관 한 줄 + 접속함, 살림방(home)엔 등 둘레의 누런
     * 얼룩. 세트를 안 준 방은 그대로. 전부 정적이라 한 메시로 합친다 — 드로우콜 하나 */
    const pipeParts: THREE.BufferGeometry[] = [], stainParts: THREE.BufferGeometry[] = [];
    for (const r of ROOMS) {
      const fs = (r as { finish?: string }).finish;
      if (!fs) continue;
      const y = (r.y ?? 0) + WALL_H;
      const x0 = Math.min(r.x1, r.x2), x1 = Math.max(r.x1, r.x2), z0 = Math.min(r.z1, r.z2), z1 = Math.max(r.z1, r.z2);
      if (fs === 'shop' || fs === 'store') {
        // 긴 변을 따라 벽에서 25cm 떨어진 전선관(지름 2.5cm)과 접속함 둘
        const alongX = x1 - x0 >= z1 - z0;
        const len = (alongX ? x1 - x0 : z1 - z0) - 0.4;
        const pipe = new THREE.CylinderGeometry(0.0125, 0.0125, len, 8);
        if (alongX) pipe.rotateZ(Math.PI / 2); else pipe.rotateX(Math.PI / 2);
        pipe.translate(alongX ? (x0 + x1) / 2 : x0 + 0.25, y - 0.05, alongX ? z0 + 0.25 : (z0 + z1) / 2);
        pipeParts.push(pipe);
        for (const t of [0.22, 0.74]) {
          const box = new THREE.BoxGeometry(0.1, 0.06, 0.1);
          box.translate(alongX ? x0 + 0.2 + len * t : x0 + 0.25, y - 0.05, alongX ? z0 + 0.25 : z0 + 0.2 + len * t);
          pipeParts.push(box);
        }
      } else if (fs === 'home') {
        // 등 둘레 누런 얼룩 — 세월. 천장 바로 밑에 얇은 원판
        const st = new THREE.CircleGeometry(0.55, 18);
        st.rotateX(Math.PI / 2);
        st.translate((x0 + x1) / 2, y - 0.004, (z0 + z1) / 2);
        stainParts.push(st);
      }
    }
    if (pipeParts.length) { const pm = mergeGeometries(pipeParts, false)!; for (const g of pipeParts) g.dispose(); ceiling.add(new THREE.Mesh(pm, new THREE.MeshStandardMaterial({ color: '#8f8f8a', roughness: 0.6, metalness: 0.4 }))); }
    if (stainParts.length) { const sm = mergeGeometries(stainParts, false)!; for (const g of stainParts) g.dispose(); ceiling.add(new THREE.Mesh(sm, new THREE.MeshStandardMaterial({ color: '#9a8a66', roughness: 1, transparent: true, opacity: 0.28, depthWrite: false }))); }
  }
  root.add(ceiling);

  /**
   * 이 벽 세그먼트가 **외벽**이면 실내 쪽 한 면(`-1`/`+1`)을, 아니면 `0`(양면)을
   * 돌려준다. `wall_trim` 의 `sides` 로 넘어간다.
   *
   * 왜 필요한가 — 허리몰딩은 벽면에서 3.8cm 나오는데 외벽 마감(함석 3.4cm)이
   * 그보다 얇다. 4mm 튀어나온 흰 띠가 별동 32m 를 통째로 가로질러 화면에서
   * **1px 검은 실선**이 됐다. 밖에서만 지우면 되고, 실내 몰딩은 그대로 둔다.
   *
   * 조각의 로컬 +Z 는 가로벽이면 월드 +Z, 세로벽(rot +90°)이면 월드 +X 다.
   */
  const interiorSide = (cx: number, cz: number, horizontal: boolean): number => {
    const E = 0.35;
    for (const b of BUILDINGS) {
      const [x0, x1] = b.x1 < b.x2 ? [b.x1, b.x2] : [b.x2, b.x1];
      const [z0, z1] = b.z1 < b.z2 ? [b.z1, b.z2] : [b.z2, b.z1];
      if (cx < x0 - E || cx > x1 + E || cz < z0 - E || cz > z1 + E) continue;
      const [lo, hi] = horizontal ? [z0, z1] : [x0, x1];
      const c = horizontal ? cz : cx;
      if (Math.abs(c - lo) < E) return 1;   // 바깥은 -쪽 -> 실내는 +쪽
      if (Math.abs(c - hi) < E) return -1;
    }
    return 0;
  };

  /* **벽 마감 세트** — 표는 `shared/finishes.ts`. 표면은 `materials.ts` 의 절차 파이프라인
   * (높이→노멀·거칠기)으로 굽는다. 처음 넣었던 캔버스 단색판은 노멀도 거칠기도 없어
   * 플라스틱이었다(2026-09-14, Chris: 「벽 마감 퀄이 너무 구리다」). */
  // --- 벽 + 건축 디테일 ---
  const plaster = get('plaster');
  const trimS = get('darkwood');

  /* ────────────────────────────────────────────────────────────────
   * **벽만 따로 굽는다** (2026-09-14, Chris: 「문을 잡고 이동하면 렉 오지게 걸리고
   * 한참 후에 문이 움직임」).
   *
   * 창·문을 옮기면 벽에 낸 구멍이 따라와야 하는데, 그러려면 구운 벽을 다시 짜야 한다.
   * 처음엔 `buildWorld` 를 통째로 다시 불렀다 — 가구 400점과 조사물 119개까지 전부 다시
   * 지으니 몇 초가 걸렸고, 그게 곧 「렉」이었다.
   * 벽·몰딩·마감만 **제 배치기**에 담아 제 그룹에 flush 하면, 다시 짓는 것이 그 그룹뿐이다.
   * 바닥·가구·조사물은 그대로 둔다.
   *
   * 충돌체는 여기서 만들지만 다시 지을 때는 **벽 몫만** 걷어내고 다시 넣어야 한다 —
   * 통째로 비우면 가구 충돌체까지 날아간다. 그래서 벽 몫의 개수를 따로 센다.
   * ──────────────────────────────────────────────────────────────── */
  const wallGroup = new THREE.Group(); wallGroup.name = 'walls';
  root.add(wallGroup);
  /** 벽+문틀+문짝을 한꺼번에 굽는 함수. 문 구간이 아래에 있어 나중에 채운다 */
  let emitWallGroup: (bt: StaticBatcher) => void = () => {};
  /**
   * **에디터에서 세운 칸막이(`partition_wall` 조각)도 마감을 받는다** (Chris 지적 5번).
   * 조각은 가구 배치에 구워져 마감 띠가 안 붙었다 — 새로 짠 방이 맨벽이었다. 조각의 자리·회전·
   * 길이(scale x)로 가짜 벽 토막을 만들어 WALLS 와 **같은 띠 코드**를 태운다. 벽 자체는 안 그린다.
   */
  const partitionWalls = (): WallDef[] => {
    const out: WallDef[] = [];
    for (const p of PIECES as (PieceDef & { _hidden?: boolean; scale3?: [number, number, number]; scale?: number; dims?: { h?: number } })[]) {
      if (p.piece !== 'partition_wall' || p._hidden) continue;
      /* **벽 높이가 아닌 partition_wall(납작한 판)은 마감을 받으면 안 된다** (사흘 v8 — hs_store 점검구 판).
       * 마감 띠는 WALL_T/2+0.008 앞에 2.6m 높이로 그려져, 12cm 판을 통째로 덮어 벽과 구분이 없어진다.
       * dims.h 가 벽보다 낮으면 판 취급 — 띠 없이 조각만 그린다. */
      if (p.dims?.h !== undefined && p.dims.h < WALL_H - 0.2) continue;
      const len = (p.scale3?.[0] ?? p.scale ?? 1) * 1.0;   // 조각 기본 길이 1m
      const rot = p.rot ?? 0, cx = p.pos[0], cz = p.pos[2];
      const dx = Math.cos(rot) * len / 2, dz = -Math.sin(rot) * len / 2;
      out.push({ x1: cx - dx, z1: cz - dz, x2: cx + dx, z2: cz + dz, y: p.pos[1], base: p.pos[1], h: 2.6, _finishOnly: true } as WallDef & { _finishOnly: boolean });
    }
    return out;
  };
  const emitWalls = (bt: StaticBatcher) => {

  for (const w of [...WALLS, ...partitionWalls()]) {
    const dx = w.x2 - w.x1;
    const dz = w.z2 - w.z1;
    const len = Math.hypot(dx, dz);
    const h = w.h ?? WALL_H;
    const y = (w.y ?? 0) + h / 2;
    const horizontal = Math.abs(dx) > Math.abs(dz);
    const cx = (w.x1 + w.x2) / 2;
    const cz = (w.z1 + w.z2) / 2;
    const finishOnly = !!(w as { _finishOnly?: boolean })._finishOnly;   // 에디터 칸막이 — 띠만

    if (!finishOnly) bt.add(
      worldUV(new THREE.BoxGeometry(horizontal ? len : WALL_T, h, horizontal ? WALL_T : len), plaster.tile),
      plaster.material,
      V(cx, y, cz),
    );

    /* 벽 토막 분류 (2026-09-10, Chris: 「창문 위쪽은 왜 다 통일성이 없냐」).
     * 예전엔 `!w.y` 인 토막에만 몰딩·충돌체를 달았다 — 그래서 창·문 **위 헤더**(y=2.15·2.2)에 크라운이
     * 빠져 띠가 창마다 끊겼고, 언덕 위 집(y=3.6)은 벽 전체에 몰딩도 **충돌체도** 없었다.
     * 바닥(base)은 레벨이 준다. 통벽 = 3단 몰딩, 헤더 = 크라운만, 허리벽 = 걸레받이 + 창턱. */
    const base = w.base ?? 0;
    const y0 = w.y ?? base;
    const full = h >= WALL_H - 0.05;
    const header = !full && y0 + h >= base + WALL_H - 0.05;   // 창·문 위
    const sides = interiorSide(cx, cz, horizontal);
    /* **마감 한 겹.** 몰딩의 허리선(1.02)을 경계로 아래는 징두리, 위는 벽 색.
     * 방 안쪽(`sides`)에만 덧대고, 벽면에서 1cm 띄워 z-fighting 을 피한다.
     * 창·문 토막(`gap`)은 건너뛴다 — 그 자리는 유리와 문짝이 차지한다. */
    {
      /* **창 아래·위 토막(`gap`)도 칠한다.** 처음엔 「그 자리는 유리와 문짝이 차지한다」고 건너뛰었는데,
       * gap 토막은 개구부가 아니라 개구부 **주변의 벽**이다 — 창 둘레만 갈색 맨벽으로 남았다
       * (2026-09-14 눈높이 컷). 토막의 절대 높이 구간과 띠(징두리·벽)를 교차해서 칠한다. */
      /* **양쪽을 다 보고, 방 경계로 자른다.**
       * `interiorSide` 는 건물 외벽만 판정한다(방 사이 칸막이는 0) — 면마다 그 면이 보는 방을
       * 따로 본다. 그리고 벽 한 토막은 **방 하나보다 길 수 있다**(북벽 한 줄이 거실+주방을 지난다).
       * 토막 가운데가 든 방으로 통째로 칠했더니 옆방까지 마감이 번졌다(2026-09-14, Chris:
       * 「송이경의 집 거실을 지정했는데 다른 방까지 영역 침범」). 토막을 방 폭으로 잘라 그
       * 방 몫만 칠한다. */
      const CH = 1.02, OFF = WALL_T / 2 + 0.008;
      const a0 = horizontal ? Math.min(w.x1, w.x2) : Math.min(w.z1, w.z2);
      const a1 = horizontal ? Math.max(w.x1, w.x2) : Math.max(w.z1, w.z2);
      for (const side of [-1, 1] as const) {
        const inX = horizontal ? 0 : side, inZ = horizontal ? side : 0;
        const px = cx + inX * 0.45, pz = cz + inZ * 0.45;   // 이 면이 보는 쪽 점
        for (const rm of ROOMS) {
          const rmx = rm as { finish?: string; wall?: string; wains?: string | null };
          if (!rmx.finish && !rmx.wall) continue;
          const rx0 = Math.min(rm.x1, rm.x2), rx1 = Math.max(rm.x1, rm.x2);
          const rz0 = Math.min(rm.z1, rm.z2), rz1 = Math.max(rm.z1, rm.z2);
          // 이 면이 그 방 쪽을 보는가(벽에서 45cm 들어간 점이 방의 가로 띠 안)
          const faces = horizontal ? (pz >= rz0 - 0.05 && pz <= rz1 + 0.05) : (px >= rx0 - 0.05 && px <= rx1 + 0.05);
          if (!faces) continue;
          // 토막 구간 ∩ 방의 이 축 구간 — 이 겹침만 칠한다
          const r0 = horizontal ? rx0 : rz0, r1 = horizontal ? rx1 : rz1;
          const s0 = Math.max(a0, r0 - 0.12), s1 = Math.min(a1, r1 + 0.12);
          if (s1 - s0 < 0.05) continue;
          const fs = rmx.finish ? FINISH_BY_ID[rmx.finish] : undefined;
          // 방이 따로 고른 벽·징두리가 세트를 덮는다(Chris: 「벽 세트 / 벽지만」). wains null = 징두리 없음
          const wallId = rmx.wall ?? fs?.wall;
          const wainsId = rmx.wains !== undefined ? rmx.wains : fs?.wains;
          if (!wallId) continue;
          const chair = fs?.chairY ?? CH;
          const mid = (s0 + s1) / 2, seg = s1 - s0;
          // 띠는 방 바닥 기준 절대 높이. 토막(y0..y0+h)과 교차한 만큼만 낸다 — 창 위 헤더는 벽 띠만, 창 아래는 징두리만
          const bands: [number, number, SurfaceId][] = wainsId
            ? [[base, base + chair, wainsId as SurfaceId], [base + chair, base + WALL_H, wallId as SurfaceId]]
            : [[base, base + WALL_H, wallId as SurfaceId]];
          for (const [bA, bB, sid] of bands) {
            const yA = Math.max(bA, y0), yB = Math.min(bB, y0 + h);
            if (yB - yA < 0.02) continue;
            const sm = get(sid);
            const geo = new THREE.BoxGeometry(horizontal ? seg : 0.012, yB - yA, horizontal ? 0.012 : seg);
            worldUV(geo, sm.tile);   // 표면마다 제 타일 크기 — 결·줄눈이 실치수로 선다
            bt.add(geo, sm.material, V(horizontal ? mid : cx + inX * OFF, (yA + yB) / 2, horizontal ? cz + inZ * OFF : mid));
          }
        }
      }
    }
    if (finishOnly) continue;   // 에디터 칸막이는 몰딩·충돌체도 조각이 이미 가지고 있다
    if (full) {
      bakePiece(bt, 'wall_trim', { len, h, wall: WALL_T, chairY: 1.02, crownY: WALL_H - 0.09, sides }, V(cx, base, cz), horizontal ? 0 : Math.PI / 2, renderer);
    } else if (header) {
      // 헤더는 크라운만 — 걸레받이·허리몰딩을 달면 창구멍을 가로지른다
      bakePiece(bt, 'wall_trim', { len, h: WALL_H, wall: WALL_T, skirtH: 0, chairY: 0, crownY: WALL_H - 0.09, sides }, V(cx, base, cz), horizontal ? 0 : Math.PI / 2, renderer);
    } else {
      // 창 밑 허리벽 — 걸레받이만 + 창턱
      bakePiece(bt, 'wall_trim', { len, h, wall: WALL_T, chairY: 0, crownY: 0, sides }, V(cx, base, cz), horizontal ? 0 : Math.PI / 2, renderer);
      bt.add(
        worldUV(new THREE.BoxGeometry(horizontal ? len : WALL_T + 0.16, 0.06, horizontal ? WALL_T + 0.16 : len), trimS.tile),
        trimS.material,
        V(cx, y0 + h + 0.03, cz),
      );
    }
    if (!header) {
      colliders.push({
        minX: Math.min(w.x1, w.x2) - WALL_T / 2,
        maxX: Math.max(w.x1, w.x2) + WALL_T / 2,
        minZ: Math.min(w.z1, w.z2) - WALL_T / 2,
        maxZ: Math.max(w.z1, w.z2) + WALL_T / 2,
        enabled: true,
        _wall: true,   // 벽만 다시 지을 때 이 표시로 골라 뺀다
      } as Collider);
    }
  }
  };
  /**
   * **천 시뮬레이션 커튼** (2026-09-15, Chris: 「커튼 같은걸 진짜 천처럼 만들 순 없냐」).
   * tools/blender/curtain_panel.py 가 한 폭을 봉에 걸어 떨어뜨려 굽은 GLB(1.1k 정점, 11KB)를
   * 창마다 두 폭씩 복제해 **한 메시로 병합**한다 — 창 40개여도 draw call 은 하나. 색은 정점색.
   * 비동기(GLB)라 벽 배치와 따로 오지만 같은 그룹에 들어가므로 rebuildWallMesh 가 함께 지운다.
   */
  const curtains = new CurtainSystem();
  const emitCurtains = () => {
    curtains.clear();
    const H = WIN_Y.head - WIN_Y.sill, EPS = 0.2;
    const dressSeq = new Map<string, number>();   // 방마다 커튼 창 순번 — place-solve 의 attach 키와 같아야 한다
    for (const win of WINDOWS) {
      const wy = win.y ?? WIN_Y.sill, W = (win.half ?? WIN_Y.half) * 2;
      for (const r of ROOMS) {
        if (r.dress?.kind !== 'curtain') continue;
        let inward: [number, number] | null = null;
        if (win.along === 'x') { if (win.x < r.x1 - EPS || win.x > r.x2 + EPS) continue; if (Math.abs(win.z - r.z1) < EPS) inward = [0, 1]; else if (Math.abs(win.z - r.z2) < EPS) inward = [0, -1]; }
        else { if (win.z < r.z1 - EPS || win.z > r.z2 + EPS) continue; if (Math.abs(win.x - r.x1) < EPS) inward = [1, 0]; else if (Math.abs(win.x - r.x2) < EPS) inward = [-1, 0]; }
        if (!inward) continue;
        const open = r.dress.open ?? 0.7;
        const inV = new THREE.Vector3(inward[0], 0, inward[1]);
        const lat = new THREE.Vector3(-inward[1], 0, inward[0]);   // 벽을 따라
        const wallFace = WALL_T / 2;                                 // 창 중심에서 벽면까지
        const top = H + 0.14;                                        // 봉 높이(창턱 기준) — 절차 봉과 같은 규약
        const floorY = wy - WIN_Y.sill;
        const panelW = 0.42 + (1 - open) * (W / 2 - 0.2);
        /* **상호작용 호스트** (2026-09-17). place-solve 가 `dress:<방>:<n>` attach lid 를 만들어
         * 둔다 — 여기 open:왼폭/오른폭 그룹을 달면 props.attachToPart 가 경첩을 끼우고,
         * CurtainSystem 이 경첩의 slide 만큼 천을 옆으로 옮긴다. 호스트 로컬 축: +X = 벽 따라(lateral), −Z = 방 안. */
        const n = dressSeq.get(r.id) ?? 0; dressSeq.set(r.id, n + 1);
        const rot = inward[0] === 1 ? -Math.PI / 2 : inward[0] === -1 ? Math.PI / 2 : inward[1] === 1 ? Math.PI : 0;
        const host = new THREE.Group();
        host.name = `dress:${r.id}:${n}`;
        host.position.set(win.x, wy, win.z);                        // 창턱 중앙
        host.rotation.y = rot;
        host.userData.pieceKey = `dress:${r.id}:${n}`;
        host.userData.pieceId = 'curtain_pair';
        host.userData.pieceDims = { w: W, h: H, open, panels: 1 };
        wallGroup.add(host);
        for (const sx of [-1, 1]) {
          const lateral = sx * (W / 2 + 0.08 - (1 - open) * (W / 4));
          const topP = new THREE.Vector3(win.x, wy + top, win.z).addScaledVector(lat, lateral).addScaledVector(inV, wallFace);
          const og = new THREE.Group();
          og.name = `open:${sx < 0 ? '왼폭' : '오른폭'}`;
          host.add(og);
          // attachToPart 가 히트박스 크기를 잴 때 쓰는 천 자리 표시 — 안 보이는 얇은 상자
          const mk = new THREE.Mesh(new THREE.BoxGeometry(panelW, top + 0.1, 0.16));
          mk.visible = false;
          mk.position.set(lateral, (top - 0.1) / 2, -(wallFace + 0.22));
          og.add(mk);
          curtains.add({
            top: topP, lateral: lat, inward: inV,
            width: panelW, fabricWidth: panelW * 1.28,   // 1.7 이면 아래로 갈수록 원단 폭까지 퍼져 텐트가 된다
            length: top + 0.10,                                     // 창 길이 — 첫 디자인. 창턱 아래 10cm
            floorY, wallBack: 0, hang: 0.16 + 0.06, color: r.dress.color ?? '#d8ccb0', mirror: sx > 0,   // 봉(bakePiece off)과 같은 22cm — 창턱 앞
          }, wallGroup, og);
        }
        break;
      }
    }
  };

  /** 벽만 다시 굽는다 — 창·문을 옮겼을 때. 바닥·가구·조사물은 그대로 둔다 */
  const rebuildWallMesh = () => {
    const mats = new Set<THREE.Material>();
    wallGroup.traverse((o2) => {
      const m = o2 as THREE.Mesh;
      if (m.geometry) m.geometry.dispose();
      const mm = m.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mm)) mm.forEach((x) => mats.add(x)); else if (mm) mats.add(mm);
    });
    // 재질은 `surfaces()` 가 캐시해 돌려주는 공용물이라 **버리면 안 된다** — 다른 벽·바닥도 쓴다
    wallGroup.clear();
    /* **벽 몫만 걷어낸다.** 벽은 가구보다 먼저 들어가므로 「앞에서 n개」로 자르면 가구 충돌체가
     * 통째로 날아간다 — 표시를 달아 두고 그것만 뺀다(순서에 기대지 않는다) */
    for (let i = colliders.length - 1; i >= 0; i--) if ((colliders[i] as { _wall?: boolean })._wall) colliders.splice(i, 1);
    const bt = new StaticBatcher();
    emitWallGroup(bt);
    bt.flush(wallGroup);
    emitCurtains();
  };


  // --- 문틀 ---
  // 개구부 좌표는 레벨이 준다. 예전에는 저택 좌표가 박혀 있어서, 다른 레벨에서는
  // 방 한가운데에 문틀이 혼자 서 있었다.
  //
  // 예전에는 여기서 박스 세 개(선대 둘 + 상부 하나)를 놓았다. 그건 문틀이 아니라
  // 개구부에 세운 각재였다 — 문선도 멈춤턱도 없어서 벽에 낸 구멍처럼 보였다.
  // 조각으로 갈아 끼운다 (client/src/furniture/arch.ts). 정적이므로 굽는다.
  //
  // **문짝도 같이 굽는다.** 문틀만 놓으면 개구부가 검은 구멍으로 남는다 — 마을의
  // 문이 전부 그 상태였다. `door_panel` 은 「움직이는 부품이라 굽지 않는다」고
  // 적혀 있지만 그건 props.ts 가 힌지 그룹에 얹어 주는 문 이야기고, v7 의
  // PROPS 는 비어 있다. 여는 문이 이미 놓인 자리만 비우고 나머지는 닫힌 채로 굽는다.
  const hinged = PROPS.filter((p) => p.kind === 'door');

  /**
   * 이 개구부가 **어느 건물의 것이고 어느 쪽이 바깥인가.**
   *
   * `DOORWAYS` 는 wallRun 이 뱉는 납작한 좌표 목록이라 건물 정보가 없다. 그래서
   * 마을의 문 열세 짝이 전부 같은 조각(`door_panel`, 90 년대 시골집 목문)이었다 —
   * 1998 년에 닫은 분교에도, 2024 년에 지은 조합 주택에도, 함석 작업동에도.
   * 점-내부 판정으로 건물을 되찾아 성격에 맞는 문을 고른다.
   *
   * `outward` 는 실외 방향(-1/+1). 조각 규약이 **로컬 -Z 가 실외**이므로
   * 이 값이 있어야 함석문 보강대를 바깥에 댈 수 있다. 실내문이면 0 이다.
   */
  const doorSite = (x: number, z: number) => {
    const E = 0.35;
    for (const b of BUILDINGS) {
      const [x0, x1] = b.x1 < b.x2 ? [b.x1, b.x2] : [b.x2, b.x1];
      const [z0, z1] = b.z1 < b.z2 ? [b.z1, b.z2] : [b.z2, b.z1];
      if (x < x0 - E || x > x1 + E || z < z0 - E || z > z1 + E) continue;
      let outward = 0;
      if (Math.abs(x - x0) < E) outward = -1;
      else if (Math.abs(x - x1) < E) outward = 1;
      else if (Math.abs(z - z0) < E) outward = -1;
      else if (Math.abs(z - z1) < E) outward = 1;
      return { arch: b.archetype, outward };
    }
    return { arch: 'house' as const, outward: 0 };
  };

  /* **문틀·문짝도 벽과 같이 다시 굽는다** (2026-09-14, Chris: 「문틀도 같이 이동하게」).
   * 개구부를 옮기면 구멍은 따라오는데 문틀과 문짝은 본 배치에 구워져 있어 제자리에 남았다 —
   * 구멍만 옆으로 가고 문은 그대로인 꼴이다. 벽 그룹에 같이 담는다. */
  /** 문 위 높이. `level-saholv7.ts` 의 DOOR_HEAD 와 같은 값 — 그쪽이 export 를 안 해 여기 둔다 */
  const DOOR_HEAD = 2.2;
  const emitDoorways = (bt: StaticBatcher) => {
  for (const d of DOORWAYS) {
    const y = d.y ?? 0;
    const site = doorSite(d.x, d.z);
    // 로컬 -Z 를 실외로 돌린다. rotY(t): (0,0,-1) -> (-sin t, 0, -cos t).
    // 실내문은 바깥이 없으니 예전 규칙(along 만 보기)을 그대로 쓴다.
    const rot = site.outward === 0
      ? (d.along === 'x' ? 0 : Math.PI / 2)
      : d.along === 'z'
        ? -site.outward * (Math.PI / 2)
        : site.outward < 0 ? 0 : Math.PI;

    // 폭은 개구부가 준다(사흘 v7 은 1.0). half 가 없는 옛 레벨은 예전 값 1.9
    bakePiece(bt, 'door_casing', { w: (d.half ?? 0.95) * 2, h: DOOR_HEAD, wall: WALL_T }, V(d.x, y, d.z), rot, renderer);

    if (hinged.some((p) => Math.hypot(p.pos[0] - d.x, p.pos[2] - d.z) < 0.8)) continue;

    /* **문짝 크기는 개구부에서 나온다** (2026-09-14, Chris: 「문 자체를 졸라 크게 만들고
     * 지금 야메로 가려뒀네」). 여기 1.82 x 2.14 가 박혀 있었다 — 옛 레벨의 1.9m 개구부에
     * 맞춘 수다. 사흘 v7 의 개구부는 **1.0m** 라, 폭 1.82m 짜리 문짝이 1.0m 구멍을 덮는
     * 「벽에 붙인 판때기」가 됐다. 개구부에서 뽑고 문선 두께만큼만 뺀다.
     * 같은 크기로 깎으면 문틀 문선과 면이 겹쳐 z-fighting 이 난다. */
    const OW = (d.half ?? 0.95) * 2, OH = DOOR_HEAD;
    const W = Math.max(0.5, OW - 0.08), H = Math.max(1.2, OH - 0.06);
    if (site.arch === 'shed') {
      // 목공실·도예실·가마실. 살림집 문을 달면 작업동이 창고가 아니라 옆집이 된다.
      bakePiece(bt, 'door_shed', { w: W, h: H, t: 0.045, rail: 0.13, knobY: 1.02 }, V(d.x, y, d.z), rot, renderer);
    } else if (site.arch === 'school' && site.outward !== 0) {
      // 분교 현관·후문. **외짝 목문을 달면 안 된다** — 개구부가 1.9m 라 폭 1.9m 짜리
      // 문이 되고, 화면에서는 벽에 붙인 주황색 판때기로 읽혔다. 학교 현관은 두 짝
      // 알루미늄 유리문이다.
      bakePiece(
        batch,
        'door_school',
        { w: W, h: H, t: 0.055, stile: 0.075, top: 0.09, kick: 0.34, railY: 1.42, rail: 0.08, knobY: 1.02 },
        V(d.x, y, d.z),
        rot,
        renderer,
      );
    } else if (site.arch === 'school') {
      // 교실·사무실 문은 목문이 맞다 — 1998 년에 달린 문이다.
      bakePiece(
        batch,
        'door_panel',
        { w: W, h: H, t: 0.08, stile: 0.14, top: 0.16, bottom: 0.24, mid: 0.12, midY: 1.02, knobY: 1.02 },
        V(d.x, y, d.z),
        rot,
        renderer,
      );
    } else {
      // 조합 주택 여섯 채. 현관만 유리 슬릿을 넣는다 — 실내문에 유리를 넣으면
      // 밤에 방마다 밝은 세로줄이 서서 어디가 현관인지 안 읽힌다.
      bakePiece(
        batch,
        'door_flush',
        { w: W, h: H, t: 0.06, rail: 0.1, glassW: site.outward === 0 ? 0 : 0.18, glassH: 1.32, knobY: 1.02 },
        V(d.x, y, d.z),
        rot,
        renderer,
      );
    }
  }

  /**
   * 현관 디지털 도어록 (HS19).
   *
   * 조각은 카탈로그에 있는데 **월드에 한 번도 붙지 않았다.** 03_맵.md §22-4
   * 「현관문만 디지털 도어록으로 자동잠금」이 사건의 뼈대다 — 16일 밤 한서진이
   * 직접 열어 줬고, 배정호는 그냥 나갔고, 문은 닫히며 잠겼다. 19일에는 조합센터
   * 키 캐비닛의 비상 기계식 열쇠로 들어간다.
   *
   * **다섯 채 전부에 단다.** 한서진 집 것이 단서인 이유가 「파손이 하나도
   * 없다」인데, 같은 규격이 네 개 더 붙어 있어야 그 멀쩡함이 비교로 읽힌다.
   * 조합 주택은 같은 해에 같은 도면으로 지었다.
   *
   * 조각 규약: `hangs + sunk` — 원점이 상단 부착점이고 로컬 -Z 가 바깥이다.
   * 문짝(`door_panel`)의 손잡이는 로컬 +X 쪽 선대에 있으므로 같은 쪽에 단다.
   */
  for (const b of BUILDINGS) {
    if (b.archetype !== 'house') continue;
    for (const e of b.entrances) {
      // `along:'z'` 는 벽이 z 를 따라 뻗었다는 뜻이고 곧 **x 면**이다 (배정호 집).
      const onX = e.along === 'z';
      const outward = onX
        ? (e.x <= (b.x1 + b.x2) / 2 ? -1 : 1)
        : (e.z <= (b.z1 + b.z2) / 2 ? -1 : 1);
      // 로컬 -Z 를 바깥으로 돌린다. rotY(t): (0,0,-1) -> (-sin t, 0, -cos t)
      const rot = onX ? -outward * (Math.PI / 2) : outward < 0 ? 0 : Math.PI;
      /**
       * **문짝과 같은 rot 으로 위치를 계산한다.** 처음엔 「가로문이면 월드 +X」로
       * 박아 뒀는데, 그건 문짝 rot 이 항상 0 일 때만 맞는 말이었다. 문을 건물별로
       * 나누면서 rot 이 실외 방향을 따라가게 됐고, 그 순간 도어록만 반대쪽 —
       * 경첩 쪽에 붙는다. 로컬 오프셋을 돌려서 쓴다.
       */
      const u = 0.72;                       // 문짝 로컬 +X (손잡이 쪽)
      const OUT = 0.045;                    // 문짝 겉면(±0.03) 바로 앞 = 로컬 -Z
      const y = (b.baseY ?? 0) + 1.28;      // 몸체 0.98~1.28, 바 손잡이 1.07
      const s = Math.sin(rot), c = Math.cos(rot);
      const pos = V(e.x + u * c - OUT * s, y, e.z - u * s - OUT * c);
      bakePiece(bt, 'door_lock', { w: 0.076, h: 0.3, t: 0.038 }, pos, rot, renderer);
    }
  }
  };

  /* 벽 + 문틀 + 문짝을 **한 그룹**으로 굽는다. 다시 짓는 것이 이 그룹뿐이다 */

  // --- 지붕 ---
  // **여기서는 안 짓는다.** 예전엔 이 자리에서 슬레이트 판 + 눈 판 + 골 리브를
  // `WALL_H` **절대 높이**에 얹었다. 그 뒤 `buildFacades`(architecture.ts) 가
  // 지붕을 가져가면서 — 재질을 집집이 갈고(슬레이트·함석 3색·기와), 물매·처마·
  // 홈통·용마루·박공·굴뚝까지 세우고, 무엇보다 `w.baseY` 를 지킨다 — 이 블록은
  // 그 밑에 파묻힌 **두 번째 지붕**으로 남았다. 평지 집에서는 안 보여서 아무도
  // 몰랐지만, 언덕 위 한서진 집(baseY 3.6)에서는 3.6m 아래에 그려져 **마당으로
  // 튀어나왔다** — 현관 앞 눈밭에 가로로 누운 검은 널판 수십 장이 그것이다.
  // 부감(`facadeRoofs`)에서 걷어내는 목록에도 안 들어 있어 지도까지 덮었다.
  // 벽은 world 가, 껍데기는 architecture 가 — 소유자를 하나로 둔다.

  // --- 창 ---
  // 카탈로그의 미서기창 조각을 구워 넣는다 (client/src/furniture/arch.ts).
  //
  // 예전에는 여기서 막대 다섯 개와 유리판을 직접 만들었다. 그건 창이 아니라 창 모양
  // 격자였다 — 레일도 짝도 창턱도 없어서 「열린 창」을 유리를 지우는 것으로 표현했다.
  // 조각은 짝이 실제로 밀리고, 유리는 열려도 남는다.
  //
  // 굽기 때문에 draw call 은 늘지 않는다. 창 37장이 슬롯 3개(alu/oak/pane)를 쓰므로
  // 전체가 메시 세 개로 합쳐진다. 예전 코드도 프레임을 합치고는 있었지만 유리는
  // 창마다 개별 메시였고(최대 36 draw call) transmission 까지 켜져 있어서
  // 프레임마다 씬을 한 번 더 그리게 만들고 있었다.
  /* **창도 벽 그룹에** (2026-09-14, Chris: 「창문도 같은 방식으로」). 문틀과 같은 이유다 —
   * 창을 옮기면 구멍은 따라오는데 창틀은 본 배치에 구워져 제자리에 남았다. */
  const emitWindows = (bt: StaticBatcher) => {
    /* **창·블라인드는 더 이상 굽지 않는다** (2026-09-17, Chris: 「단서가 있든 없든 싹다 상호작용」).
     * place-solve 가 같은 자리에 조각으로 놓고 auto lid 가 미서기창 짝·슬랫을 잡는다 —
     * 굽으면 조각과 겹쳐 두 번 그려진다. 커튼 봉만 여기서 굽는다(천은 emitCurtains 의 시뮬레이션). */
    /* **창 가리개 — 커튼 봉만** (2026-09-15, Chris: 「커튼이나 블라인드가 필요하지 않으려나」).
     * 창이 붙은 방 중 커튼 dress 가 있는 쪽의 실내 면에 봉을 굽는다. 천은 CurtainSystem,
     * 블라인드는 place-solve 의 조각이 맡는다. */
    for (const win of WINDOWS) {
      const wy = win.y ?? WIN_Y.sill;
      const EPS = 0.2;
      for (const r of ROOMS) {
        if (r.dress?.kind !== 'curtain') continue;
        let inward: [number, number] | null = null;   // 방 안쪽으로 향하는 단위벡터
        if (win.along === 'x') {
          if (win.x < r.x1 - EPS || win.x > r.x2 + EPS) continue;
          if (Math.abs(win.z - r.z1) < EPS) inward = [0, 1]; else if (Math.abs(win.z - r.z2) < EPS) inward = [0, -1];
        } else {
          if (win.z < r.z1 - EPS || win.z > r.z2 + EPS) continue;
          if (Math.abs(win.x - r.x1) < EPS) inward = [1, 0]; else if (Math.abs(win.x - r.x2) < EPS) inward = [-1, 0];
        }
        if (!inward) continue;
        // 창턱(window_slider.sill 0.16)이 벽면에서 내민다. 커튼은 그 앞에 걸려야 턱이 천을 뚫고 나오지 않는다
        // (Chris 2026-09-15 컷)
        const off = WALL_T / 2 + 0.16 + 0.06;
        // 조각 정면(−Z)이 방 안을 보게: −Z 를 inward 로 돌리는 y 회전
        const rot = inward[0] === 1 ? -Math.PI / 2 : inward[0] === -1 ? Math.PI / 2 : inward[1] === 1 ? Math.PI : 0;
        const tint = parseInt((r.dress.color ?? '#d8ccb0').replace('#', ''), 16);
        bakePiece(
          bt,
          'curtain_pair',
          { w: (win.half ?? WIN_Y.half) * 2, h: WIN_Y.head - WIN_Y.sill, open: r.dress.open ?? 0.7, tint, panels: 0 },
          V(win.x + inward[0] * off, wy, win.z + inward[1] * off),
          rot,
          renderer,
        );
        break;
      }
    }
  };

  /* 벽 + 문틀 + 문짝 + 창을 **한 그룹**으로 굽는다. 다시 짓는 것이 이 그룹뿐이다 */
  emitWallGroup = (bt: StaticBatcher) => { emitWalls(bt); emitDoorways(bt); emitWindows(bt); };
  const wallBatch = new StaticBatcher();
  emitWallGroup(wallBatch);
  wallBatch.flush(wallGroup);
  emitCurtains();

  /**
   * 접지 그림자.
   *
   * 방마다 그림자를 던지는 등은 **최대 두 개**다(아래 조명 절). 나머지 물건은
   * 전부 바닥에 얹힌 게 아니라 떠 있는 것처럼 보인다 — 스티커 붙인 화면이
   * 되는 가장 흔한 원인이다. 물건 밑에 검은 원 하나씩 깔아서 「닿았다」를
   * 만든다. 전부 한 메시로 합쳐 그리므로 개수가 늘어도 draw call 은 1이다.
   */
  bootMark('세계: 바닥·천장·벽·문·창');
  const contact = new ContactShadows();

  // --- 가구 ---
  for (const f of FURNITURE) {
    const s = f.surface ? get(f.surface) : null;
    const geo =
      f.shape === 'cyl'
        ? new THREE.CylinderGeometry(f.size[0] / 2, f.size[0] / 2, f.size[1], 24)
        : new THREE.BoxGeometry(f.size[0], f.size[1], f.size[2]);
    if (s) worldUV(geo, s.tile);
    batch.add(
      geo,
      s ? s.material : furnitureMaterial(f, furnCache),
      // pos.y 는 '중심'이다. 예전에는 여기서 size/2 를 더해 바닥으로 취급했는데,
      // 레벨 데이터는 전부 중심 기준으로 적혀 있어서 모든 가구가 제 높이의 절반만큼
      // 공중에 떠 있었다 (냉장고 0.95m, 사물함 0.9m, 붙박이장은 천장을 뚫었다).
      V(f.pos[0], f.pos[1], f.pos[2]),
      f.rot ?? 0,
    );
    if (f.solid) {
      colliders.push({
        minX: f.pos[0] - f.size[0] / 2,
        maxX: f.pos[0] + f.size[0] / 2,
        minZ: f.pos[2] - f.size[2] / 2,
        maxZ: f.pos[2] + f.size[2] / 2,
        enabled: true,
        circle: f.shape === 'cyl' ? { x: f.pos[0], z: f.pos[2], r: f.size[0] / 2 } : undefined,
      });
    }
    // 상자 가구는 벽에 붙는 것(선반·수납장)도 있다. **바닥에 닿은 것만** 깐다 —
    // 안 그러면 허공에 검은 원이 떠서 그림자가 아니라 얼룩이 된다
    const fy = roomAt(f.pos[0], f.pos[2])?.y ?? 0;
    const bottom = f.pos[1] - f.size[1] / 2;
    if (Math.abs(bottom - fy) < 0.08) {
      contact.add(f.pos[0], fy + 0.008, f.pos[2], Math.max(f.size[0], f.size[2]) * 0.62, 0.5);
    }
  }

  // 깎아 만든 물건. **배칭하지 않는다** — 슬롯별로 이미 합쳐져 나오고(한 점 3~5 draw
  // call), 재질이 저마다 달라서 StaticBatcher 에 넣으면 오히려 쪼개진다.
  // 이것들이 시대를 말한다. 벽과 책상 골조는 1998 년이고 여기 놓인 것만 2026 년이다.
  bootMark('세계: 가구');
  const seen = new Map<string, number>();
  /** 담는 곳 안에 들어갈 조각들 — 호스트를 다 지은 뒤에 붙인다 */
  const pendingInside: { group: THREE.Object3D; spec: { host: string; part?: string }; key: string }[] = [];
  const ZERO3 = new THREE.Vector3();
  const attachedKeys = new Set<string>(); for (const pr of PROPS as { attach?: string }[]) if (pr.attach) attachedKeys.add(pr.attach);
  const hostKeys = new Set<string>(); for (const q of PIECES as { inside?: { host: string } }[]) if (q.inside?.host) hostKeys.add(q.inside.host);
  const roomBatch = new Map<string, StaticBatcher>();
  let batchedPieces = 0;
  for (const p of PIECES) {
    /* **키를 먼저 매긴다.** 건너뛰기(`continue`) 전에 세어야 한다 — 지운 조각에서 번호를 건너뛰면
     * 그 뒤 같은 방·같은 조각의 키가 통째로 하나씩 밀려, 저장한 좌표가 **엉뚱한 물건**에 붙는다
     * (Chris 2026-09-10: 「저장하니깐 좌표랑 회전시킨게 이상하게 나옴」). `applyPlaceOverrides` 도 같은 순서다. */
    const key = pieceKey(p, seen);
    if ((p as { _hidden?: boolean })._hidden) continue;   // 배치 에디터가 지운 것
    const def = PIECE_BY_ID[p.piece];
    if (!def) {
      console.warn(`[world] 없는 가구: ${p.piece}`);
      continue;
    }
    // 자리에서 seed 를 뽑는다 — 같은 조각이라도 놓인 데가 다르면 다르게 굽힌다. 같은 자리는 늘 같은 모양(재현)
    const seed = p.dims?.seed ?? placeSeed(p.pos);
    const { group, fit } = buildPiece(
      def,
      renderer,
      { ...(p.dims ?? {}), seed },
      p.tripped ? { tripped: p.tripped } : undefined,   // 분전반 내려간 회로 (v8 A6)
    );
    group.position.set(p.pos[0], p.pos[1], p.pos[2]);
    group.rotation.y = p.rot ?? 0;
    if (p.scale3) group.scale.set(p.scale3[0], p.scale3[1], p.scale3[2]);   // 에디터 축별 크기(벽·유리벽)
    else if (p.scale && p.scale !== 1) group.scale.setScalar(p.scale);   // 에디터 크기 조절
    if (p.tilt) { group.rotation.x = p.tilt[0]; group.rotation.z = p.tilt[1]; }   // 에디터 기울기
    // **에디터가 어느 PieceDef 인지 알아야 한다.** 인게임 편집 모드(editor.ts)가 조준한
    // 물체에서 이 값을 읽어 좌표를 되돌려 쓴다 — 이게 없으면 옮긴 결과를 코드로 낼 수 없다.
    group.name = `piece:${p.piece}`;
    group.userData.pieceId = p.piece;
    group.userData.pieceRoom = p.room;
    // 배치 툴(place.html)이 저장에 쓰는 안정 키: 방/조각#같은 방 같은 조각 중 몇 번째
    group.userData.pieceKey = key;
    group.userData.pieceDims = { ...def.dims, ...(p.dims ?? {}) };   // 여닫이 pivot 보정용(openSpecFor)
    // 벽·천장에 거는 것·일부러 띄운 것 — 배치 툴의 「떠 있음」 점검이 빼야 한다(창·문짝·벽시계·걸이판)
    group.userData.pieceHung = !!(p.wall || (p as { opening?: boolean }).opening || p.intent || def.hangs || def.mount === 'wall' || def.mount === 'ceiling');
    /* **담는 곳 안에 넣은 물건은 그 부품의 자식이 된다** (2026-09-11, Chris: 「상자 안에 물건을
     * 배치하거나 서랍 안에 물건을 넣을 수 있어야」). 자식이라야 서랍을 뺄 때 안의 물건이 같이
     * 나온다 — 방바닥에 놓으면 서랍만 나오고 물건은 제자리에 남는다.
     * 호스트를 아직 안 지었으면(순서가 뒤면) 나중에 붙이려고 미뤄 둔다. */
    if (p.inside) pendingInside.push({ group, spec: p.inside, key });
    else if (opts.batchPieces && !def.glb) {
      /* **움직이지 않는 부품만 방 통에 넣는다.** 여닫이(`open:*` 그룹)·담는 곳·프롭이 붙는 부품은 그대로
       * 그룹에 남는다 — 그래야 attach/inside 가 산다. 몸통은 월드 변환을 굽고 속성을 맞춰 병합한다
       * (속성이 다르면 그 재질 통이 통째로 조용히 사라진다). */
      group.updateMatrixWorld(true);
      const rb = roomBatch.get(p.room) ?? (() => { const b = new StaticBatcher(); roomBatch.set(p.room, b); return b; })();
      const movable = (o3: THREE.Object3D) => { let q: THREE.Object3D | null = o3; while (q && q !== group) { if (q.name.startsWith('open:')) return true; q = q.parent; } return false; };
      const taken: THREE.Mesh[] = [];
      group.traverse((o3) => {
        const m = o3 as THREE.Mesh;
        if (!m.isMesh || Array.isArray(m.material) || movable(m)) return;
        let g = m.geometry.index ? m.geometry.toNonIndexed() : m.geometry.clone();
        g.applyMatrix4(m.matrixWorld);
        for (const name of Object.keys(g.attributes)) if (!['position', 'normal', 'uv', 'color'].includes(name)) g.deleteAttribute(name);
        if (!g.attributes.normal) g.computeVertexNormals();
        if (!g.attributes.uv) g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(g.attributes.position.count * 2), 2));
        if (!g.attributes.color) g.setAttribute('color', new THREE.BufferAttribute(new Float32Array(g.attributes.position.count * 3).fill(1), 3));
        rb.add(g, m.material as THREE.Material, ZERO3, 0);
        taken.push(m);
      });
      for (const m of taken) m.removeFromParent();
      batchedPieces++;
      // 남은 것(여닫이 부품)이 있거나 남이 붙는 그룹이면 씬에 둔다. 빈 그룹은 버린다
      if (group.children.length || attachedKeys.has(key) || hostKeys.has(key)) root.add(group);
    }
    else root.add(group);

    // 조각의 원점은 규약상 **바닥 접촉면**(y=0)이다. 그러니 pos.y 가 곧 이 물건이
    // 얹힌 면의 높이다 — 방바닥이든 책상 상판이든 상관없이 거기 그림자를 깐다.
    // 벽에 걸리는 것(hangs)과 벽에 붙는 것(wall)은 닿은 바닥이 없으므로 제외한다
    if (!def.hangs && !p.wall) {
      const size = new THREE.Vector3();
      fit.getSize(size);
      // 발자국보다 조금만 넓게. 크게 잡으면 책상 모서리 밖으로 원이 삐져나온다
      const r = Math.max(size.x, size.z) * 0.54;
      contact.add(p.pos[0], p.pos[1] + 0.006, p.pos[2], r, r > 0.3 ? 0.5 : 0.4);
    }
  }
  contact.flush(root);

  /* 담는 곳에 넣은 물건을 이제 붙인다 — 호스트가 다 지어진 뒤라야 찾을 수 있다.
   * 못 찾으면 바닥에 그냥 둔다(조용히 사라지면 「왜 안 보이지」가 된다). */
  for (const q of pendingInside) {
    let host: THREE.Object3D | null = null;
    root.traverse((o2) => { if (!host && o2.userData.pieceKey === q.spec.host) host = o2; });
    const h = host as THREE.Object3D | null;
    const target = h && q.spec.part ? h.getObjectByName(`open:${q.spec.part}`) ?? h : h;
    if (target) target.add(q.group);
    else { console.warn(`[world] ${q.key}: 담을 곳 '${q.spec.host}' 를 못 찾아 바닥에 둔다`); root.add(q.group); }
  }

  // 정적 지오메트리를 재질별로 한 덩어리씩 합친다 (draw call 의 압도적 다수를 여기서 없앤다)
  bootMark('세계: 조각(PIECES)');
  batch.flush(root);
  for (const [room, rb] of roomBatch) {
    const g = new THREE.Group(); g.name = `batch:${room}`; g.userData.pieceRoom = room;
    const r = ROOMS.find((q) => q.id === room);
    if (r) g.userData.center = [(r.x1 + r.x2) / 2, (r.z1 + r.z2) / 2];
    rb.flush(g, true, true);
    root.add(g);
  }
  if (batchedPieces) console.info(`[world] 정적 조각 ${batchedPieces}개를 방 ${roomBatch.size}곳 덩어리로 병합`);

  // --- 조명 ---
  // 눈 내리는 밤. 하늘은 낮게 깔린 구름이라 은은하게 전체를 들어올린다
  /**
   * **아랫빛을 검정에 두지 마라 — 발밑이 눈밭이다.** 땅색을 `#1f2124` 로
   * 두면 선형값이 0.015 라 사실상 0 이고, 수평·아래를 보는 면은 그레이딩의
   * 대비 바닥(pipeline `pivot`)에 걸려 통째로 `rgb(0,0,0)` 이 된다 — 처마 밑,
   * 마루 밑, 지붕 이음매 옆면이 다 그렇게 뚫렸다. 20cm 쌓인 눈은 밤에 가장
   * 밝은 반사면이므로 되받는 빛이 저럴 수가 없다. 하늘색의 1/3 쯤에 둔다.
   */
  /**
   * **환경맵. 이게 없어서 금속이 전부 새까맸다.**
   *
   * 운동장 기둥이 화면에서 `rgb(0,0,28)` 로 나와 「검은 판때기가 서 있다」고
   * 보고를 받았다. 재질을 하나씩 갈아 끼워 보니 범인은 조명도 후처리도 아니고
   * `metalness: 0.5` 였다 — three.js 에서 금속의 확산광은 metalness 만큼 깎이고
   * 그 자리를 **환경 반사**가 채우는데, `scene.environment` 가 없으면 반사할
   * 것이 없어서 검정이 들어온다. 씬 전체에 metalness 0.3~0.9 인 재질이 스무 개가
   * 넘는다(난로·자물쇠·창틀·양철·놋그릇). 하나씩 metalness 를 내리는 건 증상만
   * 지우는 짓이라, 반사할 하늘을 만들어 준다.
   *
   * 큐브맵을 굽지 않고 64x32 그라디언트 한 장이면 된다 — 위는 밤하늘, 지평선은
   * 안개색, 아래는 눈. r185 는 등장방형 텍스처를 `scene.environment` 에 넣으면
   * 내부에서 PMREM 으로 바꿔 준다. 세기는 확산광까지 같이 올리므로 낮게 둔다.
   */
  {
    const ec = document.createElement('canvas');
    ec.width = 64;
    ec.height = 32;
    const eg = ec.getContext('2d')!;
    const grad = eg.createLinearGradient(0, 0, 0, 32);
    grad.addColorStop(0, '#3f4d70');   // 천정 — 구름 낀 밤하늘
    grad.addColorStop(0.42, '#6d8299'); // 지평선 — 안개와 같은 색
    grad.addColorStop(0.52, '#8fa2b6');
    grad.addColorStop(1, '#c3cfdf');    // 발밑 — 20cm 쌓인 눈이 되받는 빛
    eg.fillStyle = grad;
    eg.fillRect(0, 0, 64, 32);
    const env = new THREE.CanvasTexture(ec);
    env.mapping = THREE.EquirectangularReflectionMapping;
    env.colorSpace = THREE.SRGBColorSpace;
    scene.environment = env;
    scene.environmentIntensity = 0.6;
  }

  scene.add(new THREE.HemisphereLight('#48557a', '#2a2f3a', 0.92));

  /**
   * **달빛이 그림자를 던진다.**
   *
   * 예전 주석은 「창문 없는 실내라 기여가 0」이었다 — 저택 시절 얘기고 사흘은
   * 마을이다. 처마도 문틀도 댓돌도 아무것도 안 떨어뜨리니 건물이 눈 위에
   * 「놓인」 게 아니라 종이를 오려 붙인 것으로 보였다. Chris 가 「디자인이
   * 구리다」고 한 컷의 절반이 이것이다.
   *
   * 마을 전체(200m)를 한 장에 담으면 텍셀이 10cm 라 처마 그림자가 계단이 된다.
   * **플레이어 주변 56m 만** 담고, 카메라가 4m 셀을 옮길 때 따라간다(main.ts).
   * 재굽기는 main 의 `shadowDirty` 규칙 그대로 — 가만히 서 있으면 한 장으로 산다.
   * 방향은 그대로 북서 위. 원점 기준 위치는 main 이 매 셀마다 덮어쓴다.
   */
  /**
   * **달이 주광이다** (워크스루 1-4). 0.85 로 두고 채움광(skyFill 1.5)이 그보다
   * 세면, 그림자를 지는 유일한 광원이 제일 약해져서 **그림자가 있으나 마나** 해진다 —
   * 1-3 에서 달빛 그림자를 켜고도 「약하다」고 적힌 이유가 이것이다. 달을 올리고
   * 채움을 내려 주종을 바꾼다. 채움을 0 으로 못 내리는 이유는 아래 주석에 있다.
   */
  bootMark('세계: 병합');
  const moon = new THREE.DirectionalLight('#93aade', 1.5);
  moon.name = 'moon';
  moon.position.set(-24, 26, -20);
  moon.castShadow = shadows;
  /**
   * **1024 다** (워크스루 1-6, 실측으로 정함). 2048 일 때 그림자 패스가 걷는 중
   * draw call 을 +569 올렸다 — 문서 기준 480 을 넘는다(서 있을 때 694, 걷는 중 최대
   * 1263). draw call 수는 해상도로 줄지 않지만 이 서버는 SwiftShader, 곧 CPU
   * 래스터라 **그림자 맵 픽셀 수가 그대로 비용**이다. 절반으로 내리면 픽셀 작업이
   * 1/4 이 된다. 56m 창에 1024 면 텍셀 5.5cm 라 처마 그림자는 아직 버틴다.
   */
  moon.shadow.mapSize.set(1024, 1024);
  moon.shadow.camera.left = -28; moon.shadow.camera.right = 28;
  moon.shadow.camera.top = 28; moon.shadow.camera.bottom = -28;
  moon.shadow.camera.near = 1; moon.shadow.camera.far = 110;
  moon.shadow.bias = -0.0005;
  moon.shadow.normalBias = 0.25;
  scene.add(moon);
  scene.add(moon.target);

  /**
   * **남쪽 채움광.** 달이 북서쪽(-24,26,-20)에만 있어서 +Z 를 보는 면은
   * N·L 이 음수라 기여가 통째로 0 이었다. 반구광(0.72)만 남는데 그 값은
   * 톤매핑을 지나면 사실상 0 이라, 마을의 **남쪽 면 전부가 새까만 판**으로
   * 나왔다 — 문짝이 안 보인다고 한 것도, 현관 기둥이 구멍처럼 뚫려 보인 것도
   * 이것이다. 문짝을 굽어 놓고도 화면에서 안 보였던 진짜 원인.
   * 구름 낀 하늘의 되반사라 색은 차갑고 세기는 달의 절반 아래로 둔다.
   */
  /**
   * **색을 너무 파랗게 두지 마라.** 처음에 `#7d92c4` 로 뒀더니 선형값이
   * B 가 R 의 2.75 배라, 그레이딩의 대비 바닥(pipeline.ts `pivot`)에 R 만
   * 먼저 닿아 벽 픽셀이 `(0, 34, 91)` — 붉은 채널이 통째로 0 인 형광 파랑이
   * 됐다. 밤은 파랗지만 **채널 하나가 죽을 만큼** 파랗지는 않다.
   */
  const skyFill = new THREE.DirectionalLight('#9aa8c2', 1.0);
  skyFill.position.set(16, 18, 34);
  skyFill.castShadow = false;
  scene.add(skyFill);

  // 동서 면도 같은 이유로 죽는다. 약하게 한 장 더 깐다.
  const eastFill = new THREE.DirectionalLight('#8b93ab', 0.5);
  eastFill.position.set(38, 14, -6);
  eastFill.castShadow = false;
  scene.add(eastFill);

  for (const r of ROOMS) {
    // **격자로 단다.** 예전에는 긴 축 한 줄로만 달았는데, 집집마다 칸막이가
    // 있어서 등이 없는 칸이 통째로 새까맸다. 송이경의 집은 문으로 들어선 입구
    // 칸(z -13.4~-10)에 등이 하나도 없어서, 스위치가 어디 붙었는지 보이지도
    // 않고 칸막이 판자에만 빛이 고여 커다란 흰 원으로 보였다.
    // 5m 마다 하나씩 두 축 모두 나눈다. 어느 칸에 서도 등 하나는 머리 위에 있다.
    const ry = r.y ?? 0; // 오르막 위 집은 바닥이 올라가 있다
    const w = r.x2 - r.x1;
    const d = r.z2 - r.z1;
    const nx = Math.max(1, Math.round(w / 5));
    const nz = Math.max(1, Math.round(d / 5));
    const alongX = w >= d;
    const spots: [number, number][] = [];
    for (let i = 0; i < nx; i++) {
      for (let j = 0; j < nz; j++) {
        spots.push([r.x1 + (w * (i + 0.5)) / nx, r.z1 + (d * (j + 0.5)) / nz]);
      }
    }

    // 형광등은 펜던트와 빛의 성질이 다르다. 넓게 퍼지고, 덜 떨어지고, 그림자가 무르다.
    // 폐교를 펜던트로 켜면 등 밑만 뜨겁고 방 구석이 새까매져서 저택처럼 보인다.
    const fluo = r.fixture === 'fluorescent';
    /* 색온도는 마감 세트가 준다(shared/finishes.ts light). 세트가 없으면 방 정의의 색 */
    const prof = (r as { finish?: string }).finish ? FINISH_BY_ID[(r as { finish?: string }).finish!]?.light : undefined;
    const lightColor = prof?.color ?? r.light;
    const lightK = prof?.k ?? 1;
    for (const [sx, sz] of spots) {
      // **감쇠(decay)를 1.2 로 낮춰 두면 등 바로 밑이 탄다.** 신청서 뭉치가
      // 흰 덩어리로 뭉개져 글자는커녕 종이인지도 안 보였다. 등에서 2m 인
      // 책상 위 조도가 48/2.3^1.2 = 18 이었다. 1.0 을 넘으면 흰색이 된다.
      // 이제 등이 격자로 촘촘하니 세기를 낮춰도 구석이 죽지 않는다.
      const light = fluo
        ? new THREE.SpotLight(lightColor, 19 * lightK, 18, Math.PI / 2.1, 0.93, 1.75)
        : new THREE.SpotLight(lightColor, 34 * lightK, 16, Math.PI / 2.4, 0.62, 1.85);
      light.userData.baseIntensity = light.intensity / lightK;   // setRoomLight 가 배율을 다시 곱한다
      light.position.set(sx, ry + WALL_H - 0.25, sz);
      light.target.position.set(sx, ry, sz);
      // 등이 격자가 되면서 방당 최대 6개까지 늘었다. 그림자 맵을 전부 켜면
      // 방 하나 들어갈 때마다 렌더 패스가 여섯 번 더 돈다 — 프레임이 떨어진다.
      // 앞의 둘만 그림자를 지고 나머지는 빛만 준다.
      light.castShadow = shadows && (roomLights.get(r.id)?.length ?? 0) < 2;
      // cullLights 가 껐다 켤 때 이 의도를 되살려야 한다 — castShadow 를 직접 읽으면
      // 꺼진 상태를 「그림자 없음」으로 오해해 다시 안 켜진다
      light.userData.wantsShadow = light.castShadow;
      // **씬에 넣지 않는다.** 이 객체는 이제 풀에 꽂힐 '값'이다 — 그림자 설정은 슬롯이
      // 갖고 있고, 여기서는 위치·색·세기·그림자 의사만 나른다.
      const primary = !lights.has(r.id);
      srcs.push({ light, room: r.id, primary });

      // 스포트만 쓰면 바닥만 밝고 벽이 새까맣다. 그림자 없는 필 라이트로 들어올린다.
      // **방에 하나만 단다.** 등마다 달면 광원 수가 두 배가 되어 프레임이 떨어진다.
      const extra: THREE.Light[] = [light];
      if (primary) {
        const fill = fluo
          ? new THREE.PointLight(lightColor, 11 * lightK, 20, 1.55)
          : new THREE.PointLight(lightColor, 8 * lightK, 16, 1.8);
        fill.userData.baseIntensity = fill.intensity / lightK;
        fill.position.set((r.x1 + r.x2) / 2, ry + WALL_H - 0.7, (r.z1 + r.z2) / 2);
        srcs.push({ light: fill, room: r.id, primary: true });
        extra.push(fill);
        lights.set(r.id, light);
        light.userData.siblings = [fill];
      } else {
        (lights.get(r.id)!.userData.siblings as THREE.Light[]).push(light);
      }
      roomLights.set(r.id, [...(roomLights.get(r.id) ?? []), ...extra]);

      if (fluo) {
        // 형광등: 천장에 붙은 함체 + 관 두 개. 매다는 것이 아니라 붙는 것이다.
        const len = 1.32;
        const housing = new THREE.Mesh(
          new THREE.BoxGeometry(alongX ? len : 0.3, 0.1, alongX ? 0.3 : len),
          new THREE.MeshStandardMaterial({
            color: '#c8ccd2',
            roughness: 0.45,
            metalness: 0.35,
            emissive: new THREE.Color(r.light),
            emissiveIntensity: 0.2,
          }),
        );
        housing.position.set(sx, ry + WALL_H - 0.05, sz);
        housing.name = `shade_${r.id}`;
        root.add(housing);

        for (const off of [-0.07, 0.07]) {
          const tube = new THREE.Mesh(
            new THREE.BoxGeometry(alongX ? len - 0.1 : 0.06, 0.055, alongX ? 0.06 : len - 0.1),
            new THREE.MeshBasicMaterial({ color: lightColor }),
          );
          tube.position.set(alongX ? sx : sx + off, ry + WALL_H - 0.12, alongX ? sz + off : sz);
          tube.name = `bulb_${r.id}`;
          root.add(tube);
        }
        continue;
      }

      // 펜던트: 로드 + 갓 + 전구
      const rod = new THREE.Mesh(
        new THREE.CylinderGeometry(0.013, 0.013, 0.32, 6),
        new THREE.MeshStandardMaterial({ color: '#2a2622', roughness: 0.6, metalness: 0.5 }),
      );
      rod.position.set(sx, ry + WALL_H - 0.16, sz);
      root.add(rod);

      /* **안전등**(암실)은 갓까지 붉다. 크림색 갓에 붉은빛을 0.2 로만 섞으면 흰 등으로 보인다
       * (2026-09-10, Chris: 「안에 붉은 조명」). 방 색이 붉은 계열이면 갓을 그 색으로 물들이고 발광을 올린다 */
      const lc = new THREE.Color(r.light);
      const safelight = lc.r > 0.45 && lc.g < 0.35 && lc.b < 0.35;
      const shade = new THREE.Mesh(
        new THREE.ConeGeometry(0.36, 0.3, 22, 1, true),
        new THREE.MeshStandardMaterial({
          color: safelight ? '#5a1b16' : '#efe4cd',
          side: THREE.DoubleSide,
          roughness: 0.55,
          emissive: lc,
          emissiveIntensity: safelight ? 1.1 : 0.2,
        }),
      );
      shade.position.set(sx, ry + WALL_H - 0.33, sz);
      shade.castShadow = true;
      shade.name = `shade_${r.id}`;
      root.add(shade);

      const bulb = new THREE.Mesh(
        new THREE.SphereGeometry(0.08, 14, 10),
        new THREE.MeshBasicMaterial({ color: lightColor }),
      );
      if (safelight) (bulb.material as THREE.MeshBasicMaterial).color.setHex(0xff2a18);
      bulb.position.set(sx, ry + WALL_H - 0.45, sz);
      bulb.name = `bulb_${r.id}`;
      root.add(bulb);
    }
  }

  /**
   * 시야 기반 조명 컬링.
   *
   * 실내는 벽이 시야를 끊어주는데도 three 는 모든 라이트를 모든 픽셀에 대해 계산하고,
   * 그림자 라이트는 저마다 씬을 한 번 더 그린다. 지금 방에서 보일 수 있는 방
   * (내 방 + 홀 + 열린 문 너머)만 켜면 활성 그림자 패스가 통째로 줄어든다.
   */
  bootMark('세계: 달빛·방 조명');
  let lastCull: [string | null, Set<string>, Record<string, boolean>, boolean, THREE.Vector3 | null] | null = null;
  /**
   * **방 조명 색·세기를 그 자리에서 바꾼다** — 마감 세트를 바꿨을 때. 조명은 풀 슬롯이 소스에서
   * 값을 복사해 쓰므로 소스(스포트·필)를 고치고 마지막 컬링을 다시 돌리면 화면에 바로 먹는다.
   * 등갓·형광관의 발광 색도 같이(`bulb_<room>`).
   */
  const setRoomLight = (roomId: string, color: string, k = 1) => {
    for (const l of roomLights.get(roomId) ?? []) {
      l.color.set(color);
      const base = (l.userData.baseIntensity as number | undefined) ?? l.intensity;
      l.userData.baseIntensity = base; l.intensity = base * k;
    }
    root.traverse((o) => { if (o.name === `bulb_${roomId}`) ((o as THREE.Mesh).material as THREE.MeshBasicMaterial).color.set(color); });
    if (lastCull) cullLights(...lastCull);
  };
  const cullLights = (
    roomId: string | null,
    openDoors: Set<string>,
    switches: Record<string, boolean>,
    all = false,
    at: THREE.Vector3 | null = null,
  ) => {
    lastCull = [roomId, openDoors, switches, all, at];
    // 조감도에서는 전부 켠다. 한 방만 밝은 조감도는 조감도가 아니다
    const visible = all ? new Set(ROOMS.map((r) => r.id)) : new Set<string>(['hall']);
    if (roomId) visible.add(roomId);
    // 열린 문은 양쪽 방을 서로 보이게 한다
    for (const d of openDoors) {
      const room = doorRoom()[d];
      if (room && (roomId === room || roomId === 'hall' || visible.has(room))) visible.add(room);
      if (room && roomId === room) visible.add('hall');
    }
    // 켜져 있고 보이는 방의 조명만 후보다. 슬롯보다 많으면 **가까운 것이 이긴다**
    const cands = srcs.filter((s) => visible.has(s.room) && switches[s.room] !== false);

    const rank = (s: LightSrc) => {
      // 조감도에서는 방마다 대표 조명 하나가 먼저다 — 거리로 고르면 가까운 방이 슬롯을
      // 다 먹고 먼 방이 통째로 캄캄해진다. 방 11개 < 슬롯 12개라 대표는 전부 들어간다
      if (all) return s.primary ? 0 : 1;
      if (s.room === roomId) return 0;
      if (s.room === 'hall') return 1;
      return 2;
    };
    const dist2 = (s: LightSrc) => (at ? s.light.position.distanceToSquared(at) : 0);
    // sort 는 안정적이므로(ES2019) 같은 rank 안에서는 거리순이 유지된다
    const sorted = cands.slice().sort((a, b) => rank(a) - rank(b) || dist2(a) - dist2(b));

    const pickSpot: (LightSrc | null)[] = new Array(SPOT_SLOTS).fill(null);
    // 그림자 슬롯은 **앞의 SHADOW_SLOTS 개로 고정**이다. castShadow 를 배정마다 켰다 끄면
    // 그림자 맵 개수가 흔들려 셰이더가 다시 컴파일된다 — 풀을 만든 이유가 사라진다.
    // 그래서 그림자를 원하는 조명을 그 슬롯에 **먼저** 꽂는다.
    let shadowAt = 0;
    let plainAt = SHADOW_SLOTS;
    for (const s of sorted) {
      if (!(s.light as THREE.SpotLight).isSpotLight) continue;
      const wants = s.light.userData.wantsShadow === true;
      let i = -1;
      if (wants && shadowAt < SHADOW_SLOTS) i = shadowAt++;
      else if (plainAt < SPOT_SLOTS) i = plainAt++;
      else if (shadowAt < SHADOW_SLOTS) i = shadowAt++;
      if (i < 0) break;
      pickSpot[i] = s;
    }
    for (let i = 0; i < SPOT_SLOTS; i++) {
      const sl = spotSlots[i];
      const src = pickSpot[i]?.light as THREE.SpotLight | undefined;
      if (!src) {
        // 남는 슬롯은 **끄지 않고 세기만 0** 이다. visible 을 만지면 개수가 흔들린다
        sl.intensity = 0;
        continue;
      }
      sl.position.copy(src.position);
      sl.target.position.copy(src.target.position);
      sl.color.copy(src.color);
      sl.intensity = src.intensity;
      sl.distance = src.distance;
      sl.angle = src.angle;
      sl.penumbra = src.penumbra;
      sl.decay = src.decay;
    }

    let pi = 0;
    for (const s of sorted) {
      if (!(s.light as THREE.PointLight).isPointLight) continue;
      if (pi >= POINT_SLOTS) break;
      const sl = pointSlots[pi++];
      const src = s.light as THREE.PointLight;
      sl.position.copy(src.position);
      sl.color.copy(src.color);
      sl.intensity = src.intensity;
      sl.distance = src.distance;
      sl.decay = src.decay;
    }
    for (let i = pi; i < POINT_SLOTS; i++) park(pointSlots[i]);
  };

  const _wp = new THREE.Vector3();
  const roomIds = new Set(ROOMS.map((r) => r.id));
  let visRooms: Set<string> | null = null;
  const roomOk = (room: string | undefined) => !visRooms || !room || visRooms.has(roomIds.has(room) ? room : OUT);
  // WebGPU 에선 정적 가구가 Render Bundle 아래로 들어간다(gpu.ts bundleStatic) — 한 단 더 내려가 본다
  const pieceGroups = () => root.children.flatMap((c) => ((c as { isBundleGroup?: boolean }).isBundleGroup ? c.children : [c]))
    .filter((g) => g.userData.pieceRoom || g.userData.pieceId);
  const cullPieces = (at: THREE.Vector3, radius: number) => {
    const r2 = radius * radius;
    for (const g of pieceGroups()) {
      const c = g.userData.center as [number, number] | undefined;
      if (c) { _wp.set(c[0], 0, c[1]); } else g.getWorldPosition(_wp);
      const dx = _wp.x - at.x, dz = _wp.z - at.z;
      g.userData.inRange = dx * dx + dz * dz < r2;
      g.visible = g.userData.inRange && roomOk(g.userData.pieceRoom);
    }
  };
  const setVisibleRooms = (rooms: Set<string> | null) => {
    visRooms = rooms;
    for (const g of pieceGroups()) g.visible = g.userData.inRange !== false && roomOk(g.userData.pieceRoom);
  };
  return { root, colliders, curtains, cullPieces, setVisibleRooms, lights, ceiling, cullLights, rebuildWallMesh, rebuildFloorMesh, setRoomLight, surfaceOf: (id) => get(id).material };
}

/**
 * 스위치 상태의 '시각적' 반영 — 전구와 갓만 바꾼다.
 *
 * 라이트의 visible 은 오직 cullLights 가 소유한다. 여기서도 건드리면 두 소유자가
 * 서로를 덮어써서 컬링이 무력화된다 (실제로 그림자 패스가 안 줄어들고 있었다).
 */
export function setRoomLight(built: BuiltWorld, roomId: string, _on: boolean) {
  const light = built.lights.get(roomId);
  if (!light) return;
  const on = _on;
  const color = ROOMS.find((r) => r.id === roomId)?.light ?? '#ffffff';
  built.root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    if (o.name === `bulb_${roomId}`) {
      (o.material as THREE.MeshBasicMaterial).color.set(on ? color : '#26231f');
    } else if (o.name === `shade_${roomId}`) {
      (o.material as THREE.MeshStandardMaterial).emissiveIntensity = on ? 0.2 : 0;
    }
  });
}
